import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import Pricing from "@/pages/Pricing";
import Browse from "@/pages/Browse";
import ServiceDetail from "@/pages/ServiceDetail";
import ProviderProfile from "@/pages/ProviderProfile";
import CustomerBookings from "@/pages/CustomerBookings";
import CustomerServices from "@/pages/CustomerServices";
import BusinessDashboard from "@/pages/BusinessDashboard";
import SubscriberDashboard from "@/pages/SubscriberDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import Support from "@/pages/Support";
import Security from "@/pages/Security";
import Profile from "@/pages/Profile";
import BookingSuccess from "@/pages/BookingSuccess";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import ProviderPackages from "@/pages/ProviderPackages";
import PackagesBrowse from "@/pages/PackagesBrowse";
import PackageDetail from "@/pages/PackageDetail";
import PackageSuccess from "@/pages/PackageSuccess";
import CustomerPackages from "@/pages/CustomerPackages";
import PackagesGuide from "@/pages/PackagesGuide";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Messages from "@/pages/Messages";
import SavedServices from "@/pages/SavedServices";
import CustomerDashboard from "@/pages/CustomerDashboard";
import Reactivate from "@/pages/Reactivate";
import AuthCallback from "@/pages/AuthCallback";

function Protected({ children, role }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) return <Navigate to="/" replace />;
  return children;
}

function Shell() {
  const location = useLocation();
  // Process the Google OAuth return (session_id in the fragment) BEFORE any route/guard runs.
  if (location.hash && location.hash.includes("session_id=")) return <AuthCallback />;
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1 flex flex-col">
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/reactivate" element={<Reactivate />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/browse" element={<Browse />} />
        <Route path="/packages" element={<PackagesBrowse />} />
        <Route path="/packages/:id" element={<PackageDetail />} />
        <Route path="/package/success" element={<Protected role="customer"><PackageSuccess /></Protected>} />
        <Route path="/my-packages" element={<Navigate to="/home?tab=packages" replace />} />
        <Route path="/provider/packages" element={<Protected role="subscriber"><ProviderPackages /></Protected>} />
        <Route path="/provider/packages/guide" element={<Protected role="subscriber"><PackagesGuide /></Protected>} />
        <Route path="/provider/payouts" element={<Navigate to="/settings" replace />} />
        <Route path="/service/:id" element={<ServiceDetail />} />
        <Route path="/p/:providerId" element={<ProviderProfile />} />
        <Route path="/my-bookings" element={<Navigate to="/home?tab=bookings" replace />} />
        <Route path="/my-services" element={<Navigate to="/home?tab=services" replace />} />
        <Route path="/saved" element={<Navigate to="/home?tab=saved" replace />} />
        <Route path="/home" element={<Protected role="customer"><CustomerDashboard /></Protected>} />
        <Route path="/messages" element={<Protected><Messages /></Protected>} />
        <Route path="/business" element={<Protected role="customer"><BusinessDashboard /></Protected>} />
        <Route path="/booking/success" element={<Protected role="customer"><BookingSuccess /></Protected>} />
        <Route path="/subscription/success" element={<Protected role="subscriber"><SubscriptionSuccess /></Protected>} />
        <Route path="/dashboard" element={<Protected role="subscriber"><SubscriberDashboard /></Protected>} />
        <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
        <Route path="/support" element={<Protected><Support /></Protected>} />
        <Route path="/profile" element={<Protected><Profile /></Protected>} />
        <Route path="/settings" element={<Protected><Security /></Protected>} />
        <Route path="/security" element={<Navigate to="/settings" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </main>
      <Footer />
      <PWAInstallPrompt />
    </div>
  );
}

export default function App() {
  return (
    <div className="App min-h-screen">
      <AuthProvider>
        <BrowserRouter>
          <Shell />
          <Toaster position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
